import { Fragment, useState } from "react";
import { useLocation, Link } from "react-router";
import { Menu, MenuButton, MenuItems, MenuItem, Transition, DialogTitle } from "@headlessui/react";
import { ChevronDownIcon, ArrowLeftStartOnRectangleIcon } from "@heroicons/react/20/solid";
import {
  ClipboardDocumentCheckIcon,
  MegaphoneIcon,
  PresentationChartBarIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  CogIcon,
  ChatBubbleLeftEllipsisIcon,
  VideoCameraIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";

import useUserStore from "../../store/useUserStore";
import useFeatureFlagStore from "../../store/useFeatureFlagStore";
import WithPermission from "../withPermission/WithPermission";
import Modal from "../modal/Modal";
import { PERMISSIONS } from "../../utils/constants";
import { useAuctionUrls, getAuctionUrl } from "../../hooks/useAuctionUrls";

export default function UserMenu() {
  const { pathname } = useLocation();
  const { userName = "", logout, refreshToken } = useUserStore();
  const { enableOnlineAuction } = useFeatureFlagStore();
  const { auctionUrls } = useAuctionUrls();

  const [logoutModal, setLogoutModal] = useState(false);

  const slugs = pathname.slice(1).split("/");
  let showEventActions = ["auction", "auctioneer", "ringman", "presenter", "settings", "auction-control-center"].includes(
    slugs[0],
  );
  const showOnlineAuctionActions = ["online-auction"].includes(slugs[0]);
  const event_ID = slugs[1];

  return (
    <>
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <MenuButton className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600">
            {userName}
            <ChevronDownIcon
              className="-mr-1 ml-2 h-5 w-5 text-primary-200 hover:text-primary-100"
              aria-hidden="true"
            />
          </MenuButton>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems className="z-50 absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
            <div className="px-1 py-1">
              {showOnlineAuctionActions && enableOnlineAuction && (
                <WithPermission id={PERMISSIONS.Auction_Auctioneer}>
                  <MenuItem>
                    <Link
                      to={`/auction-control-center/${event_ID}`}
                      target="_blank"
                      className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <RectangleGroupIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                      Auction Control Center
                    </Link>
                  </MenuItem>
                </WithPermission>
              )}
              {showEventActions && (
                <>
                  {slugs[0] !== "auction" && (
                    <MenuItem>
                      <Link
                        to={`/auction/${event_ID}`}
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <CalendarDaysIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Live Event
                      </Link>
                    </MenuItem>
                  )}
                  <WithPermission id={PERMISSIONS.Auction_Auctioneer}>
                    <MenuItem>
                      <Link
                        to={`/auctioneer/${event_ID}`}
                        target="_blank"
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <MegaphoneIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Auctioneer Board
                      </Link>
                    </MenuItem>
                  </WithPermission>
                  <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                    <MenuItem>
                      <Link
                        to={`/ringman/${event_ID}`}
                        target="_blank"
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <ClipboardDocumentCheckIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Virtual Ringman
                      </Link>
                    </MenuItem>
                  </WithPermission>
                  <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                    <MenuItem>
                      <Link
                        to={`/messenger/${event_ID}`}
                        target="_blank"
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <ChatBubbleLeftEllipsisIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Messenger
                      </Link>
                    </MenuItem>
                  </WithPermission>
                  <WithPermission id={PERMISSIONS.Auction_Presenter}>
                    <MenuItem>
                      <Link
                        to={`/presenter/${event_ID}`}
                        target="_blank"
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <PresentationChartBarIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Presenter Board
                      </Link>
                    </MenuItem>
                  </WithPermission>
                  {slugs[0] !== "settings" && (
                    <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                      <MenuItem>
                        <Link
                          to={`/settings/${event_ID}`}
                          target="_blank"
                          className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                        >
                          <CogIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                          Event Settings
                        </Link>
                      </MenuItem>
                    </WithPermission>
                  )}
                  <WithPermission id={PERMISSIONS.Auction_AuctionClerk}>
                    <MenuItem>
                      <Link
                        to={`/ringman/${event_ID}/video`}
                        target="_blank"
                        className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      >
                        <VideoCameraIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                        Broadcast Settings
                      </Link>
                    </MenuItem>
                  </WithPermission>
                </>
              )}
            </div>
            <div className="px-1 py-1">
              <MenuItem>
                <a
                  href={`${getAuctionUrl(auctionUrls, "myAccountURL")}?token=${refreshToken}`}
                  target="_blank"
                  className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                >
                  <UserCircleIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                  My Account
                </a>
              </MenuItem>
              <MenuItem>
                <button
                  className={`text-gray-900 data-focus:bg-primary-600 data-focus:text-white group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                  onClick={() => setLogoutModal(true)}
                >
                  <ArrowLeftStartOnRectangleIcon className="mr-2 h-5 text-primary-400" aria-hidden="true" />
                  Log out
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>
      <Modal isOpen={logoutModal} setIsOpen={setLogoutModal}>
        <>
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 rounded-t-lg">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Log out?
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">Are you sure you want to log out?</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-lg">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
              onClick={logout}
            >
              Log out
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              onClick={() => setLogoutModal(false)}
            >
              Cancel
            </button>
          </div>
        </>
      </Modal>
    </>
  );
}
